import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from 'api/v1/api';
import { StatusChip, CabinetButton } from 'ui/kit/Kit';
import { StepBody } from 'ui/kit/uiMotion';
import starEmpty from 'ui/kit/stars/star-empty.svg';
import starFilled from 'ui/kit/stars/star-filled.svg';
import {
  REVIEW_COMMENT_MAX_LENGTH,
  validateReviewCommentText,
} from 'modules/ChatV2/utils/contactFilter';

import { chatKeys } from '../api/queries';
import classes from './ReviewComposer.module.scss';

const UNPUBLISHED = 'Отзыв еще не опубликован. Он появится в профиле после проверки.';

type ReviewComposerProps = {
  tutorUserId: number;
  chatId?: number | null;
  onDone?: () => void;
};

export function ReviewComposer({ tutorUserId, chatId = null, onDone }: ReviewComposerProps) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/reviews', {
        tutor_user_id: tutorUserId,
        rating,
        comment: comment.trim() || null,
        ...(chatId != null && chatId > 0 ? { chat_id: chatId } : {}),
      });
      return response.data as { status_message?: string | null };
    },
    onSuccess: () => {
      setSent(true);
      if (chatId) {
        void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
      }
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });

  if (sent) {
    return (
      <StepBody stepKey="review-pending">
        <div className={classes.pending}>
          <StatusChip label="На проверке" />
          <p>{UNPUBLISHED}</p>
          {onDone ? (
            <CabinetButton onClick={onDone}>Вернуться</CabinetButton>
          ) : null}
        </div>
      </StepBody>
    );
  }

  const submitError = mutation.isError
    ? axiosDetail(mutation.error) || 'Не удалось отправить отзыв. Попробуй еще раз.'
    : null;

  const handleSubmit = () => {
    if (rating < 1) {
      setCommentError('Выбери, сколько звезд поставить за занятие.');
      return;
    }
    const error = validateReviewCommentText(comment.trim());
    if (error) {
      setCommentError(error);
      return;
    }
    setCommentError(null);
    mutation.mutate();
  };

  return (
    <form
      className={classes.form}
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h2>Отзыв о занятии</h2>
      <p>Звезды относятся к занятию. Это не оценка человека.</p>
      <div className={classes.stars} role="radiogroup" aria-label="Звезды за занятие">
        {[1, 2, 3, 4, 5].map((value) => {
          const on = value <= rating;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={rating === value}
              aria-label={`${value} из 5`}
              className={classes.star}
              onClick={() => {
                setRating(value);
                if (commentError === 'Выбери, сколько звезд поставить за занятие.') {
                  setCommentError(null);
                }
              }}
            >
              <img
                src={on ? starFilled : starEmpty}
                alt=""
                width="20.9232"
                height="19.8992"
              />
            </button>
          );
        })}
        <span className={classes.starNote}>{rating === 0 ? 'Нет оценок' : `${rating} / 5`}</span>
      </div>
      <label className={classes.field}>
        <span>Комментарий</span>
        <textarea
          value={comment}
          rows={5}
          maxLength={REVIEW_COMMENT_MAX_LENGTH}
          aria-invalid={Boolean(commentError)}
          onChange={(event) => {
            const next = event.target.value.slice(0, REVIEW_COMMENT_MAX_LENGTH);
            setComment(next);
            setCommentError(validateReviewCommentText(next.trim()));
          }}
        />
      </label>
      <p className={commentError || submitError ? classes.error : classes.hint}>
        {commentError ||
          submitError ||
          'Без ссылок, телефонов и контактов в мессенджерах.'}
      </p>
      <CabinetButton type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Отправляем...' : 'Отправить на проверку'}
      </CabinetButton>
    </form>
  );
}

function axiosDetail(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const detail = (error.response?.data as { detail?: string; status_message?: string } | undefined)
    ?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}
